import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Custom hook to track unseen claims for admin notifications
 * 
 * Uses localStorage to remember which claims the admin has already seen.
 * When new claims arrive or claims haven't been seen yet, shows a red dot badge.
 * 
 * @returns {Object} { unseenDancerClaims: number, unseenStudioClaims: number, loading: boolean }
 */
export const useUnseenClaims = (enabled = true) => {
  const [unseenDancerClaims, setUnseenDancerClaims] = useState(0);
  const [unseenStudioClaims, setUnseenStudioClaims] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only admins review claims – don't poll Firestore (and hit permission errors) for everyone else
    if (!enabled) {
      setUnseenDancerClaims(0);
      setUnseenStudioClaims(0);
      setLoading(false);
      return undefined;
    }

    const checkUnseenClaims = async () => {
      try {
        // Get seen claim IDs from localStorage
        const seenDancerClaimsStr = localStorage.getItem('seenDancerClaims');
        const seenStudioClaimsStr = localStorage.getItem('seenStudioClaims');
        
        const seenDancerClaims = seenDancerClaimsStr ? JSON.parse(seenDancerClaimsStr) : [];
        const seenStudioClaims = seenStudioClaimsStr ? JSON.parse(seenStudioClaimsStr) : [];

        // Fetch pending dancer claims
        const dancerClaimsQuery = query(
          collection(db, 'profileClaims'),
          where('status', '==', 'pending')
        );
        const dancerClaimsSnapshot = await getDocs(dancerClaimsQuery);
        
        // Count unseen dancer claims
        const unseenDancer = dancerClaimsSnapshot.docs.filter(
          doc => !seenDancerClaims.includes(doc.id)
        ).length;
        
        // Fetch pending studio claims
        const studioClaimsQuery = query(
          collection(db, 'studioProfileClaims'),
          where('status', '==', 'pending')
        );
        const studioClaimsSnapshot = await getDocs(studioClaimsQuery);
        
        // Count unseen studio claims
        const unseenStudio = studioClaimsSnapshot.docs.filter(
          doc => !seenStudioClaims.includes(doc.id)
        ).length;

        setUnseenDancerClaims(unseenDancer);
        setUnseenStudioClaims(unseenStudio);
        
        console.log('🔔 Unseen Claims Check:', {
          dancerClaims: unseenDancer,
          studioClaims: unseenStudio,
          totalPendingDancer: dancerClaimsSnapshot.size,
          totalPendingStudio: studioClaimsSnapshot.size
        });
      } catch (error) {
        console.error('Error checking unseen claims:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUnseenClaims();
    
    // Refresh every 2 minutes to check for new claims
    const interval = setInterval(checkUnseenClaims, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [enabled]);

  return { unseenDancerClaims, unseenStudioClaims, loading };
};

/**
 * Mark dancer claims as seen
 * @param {string[]} claimIds - Array of claim IDs to mark as seen
 */
export const markDancerClaimsAsSeen = (claimIds) => {
  try {
    const seenClaimsStr = localStorage.getItem('seenDancerClaims');
    const seenClaims = seenClaimsStr ? JSON.parse(seenClaimsStr) : [];
    
    // Add new claim IDs and remove duplicates
    const updatedSeenClaims = [...new Set([...seenClaims, ...claimIds])];
    
    localStorage.setItem('seenDancerClaims', JSON.stringify(updatedSeenClaims));
    console.log('✅ Marked dancer claims as seen:', claimIds.length);
  } catch (error) {
    console.error('Error marking dancer claims as seen:', error);
  }
};

/**
 * Mark studio claims as seen
 * @param {string[]} claimIds - Array of claim IDs to mark as seen
 */
export const markStudioClaimsAsSeen = (claimIds) => {
  try {
    const seenClaimsStr = localStorage.getItem('seenStudioClaims');
    const seenClaims = seenClaimsStr ? JSON.parse(seenClaimsStr) : [];
    
    // Add new claim IDs and remove duplicates
    const updatedSeenClaims = [...new Set([...seenClaims, ...claimIds])];
    
    localStorage.setItem('seenStudioClaims', JSON.stringify(updatedSeenClaims));
    console.log('✅ Marked studio claims as seen:', claimIds.length);
  } catch (error) {
    console.error('Error marking studio claims as seen:', error);
  }
};

/**
 * Clear all seen claims from localStorage (useful for testing or when claims are deleted)
 */
export const clearSeenClaims = () => {
  localStorage.removeItem('seenDancerClaims');
  localStorage.removeItem('seenStudioClaims');
  console.log('🗑️ Cleared all seen claims from localStorage');
};
